import { HarnessSentFile } from '@flowx/designer/modules/ai-builder/types/conversation-file.types'
import {
  HarnessTranscriptEntry,
  HarnessTurnComment,
} from '@flowx/designer/modules/ai-builder/types/harness-session.types'

/** The transcript, read as a conversation. The reducer keeps one flat list of entries in wire
 * order; the design reads it as turns — what the maker said, then everything the agent did in
 * answer, under one mark. This is that fold and nothing else; pure, so it stays a unit test. */

export type HarnessToolEntry = Extract<HarnessTranscriptEntry, { type: 'tool' }>
export type HarnessArtefactMarker = Extract<HarnessTranscriptEntry, { type: 'artefact-marker' }>
export type HarnessNotice = Extract<HarnessTranscriptEntry, { type: 'notice' }>
export type HarnessAnswerEntry = Extract<HarnessTranscriptEntry, { type: 'answer' }>

export type UserTurn = {
  type: 'user'
  at: number
  text: string
  files?: readonly HarnessSentFile[]
  comment?: HarnessTurnComment
}

export type AgentTurn = {
  type: 'agent'
  /** When the agent began to speak. Absent while it has only worked. */
  at?: number
  /** What the harness files a verdict on this turn against — the first id among its entries, since
   * the harness snapshots the whole turn from whichever one it is given. Absent until the session
   * has been re-read, which is the only place ids come from. */
  messageId?: string
  /** The work behind the reply, in order — the trace the row folds. */
  tools: readonly HarnessToolEntry[]
  text: string
  /** True while the reply is still arriving. */
  streaming: boolean
  artefacts: readonly HarnessArtefactMarker[]
  notices: readonly HarnessNotice[]
  /** What the maker settled on this turn without writing — the echo lines under it. Once a
   * turn carries answers it is closed: whatever the agent does next is its answer to them. */
  answers: readonly HarnessAnswerEntry[]
}

/** The run's progress as it stood at a point of the conversation. */
export type RunTurn = Extract<HarnessTranscriptEntry, { type: 'run' }>

export type TranscriptTurn = UserTurn | AgentTurn | RunTurn

type AgentEntry = Exclude<HarnessTranscriptEntry, { type: 'user' } | { type: 'run' }>

export function emptyAgentTurn(): AgentTurn {
  return {
    type: 'agent',
    tools: [],
    text: '',
    streaming: false,
    artefacts: [],
    notices: [],
    answers: [],
  }
}

/** A maker's message opens a turn of its own, and so does a run message, where the run's event
 * came; everything else until the next one is the agent's. */
export function groupTurns(entries: readonly HarnessTranscriptEntry[]): TranscriptTurn[] {
  return entries.reduce<TranscriptTurn[]>((turns, entry) => {
    if (entry.type === 'user') {
      return [...turns, { ...entry }]
    }

    if (entry.type === 'run') {
      return [...turns, entry]
    }

    const last = turns.at(-1)

    // An agent turn that already holds the maker's answers is closed to everything but more
    // answers: the agent's next work is its reply to them, and reads as a turn of its own. An
    // interrupt is the exception — a question and the answer it gets are one exchange, and a walk
    // through several of them is what the folded echo lines below are for.
    const open =
      last?.type === 'agent' &&
      (!last.answers.length || entry.type === 'answer' || entry.type === 'interrupt')

    return open
      ? [...turns.slice(0, -1), absorb(last, entry)]
      : [...turns, absorb(emptyAgentTurn(), entry)]
  }, [])
}

function absorb(turn: AgentTurn, entry: AgentEntry): AgentTurn {
  switch (entry.type) {
    case 'tool':
      return { ...turn, tools: [...turn.tools, entry] }

    // Text interrupted by a tool call and resumed reads as one reply, paragraph by paragraph.
    case 'assistant':
      return {
        ...turn,
        at: turn.at ?? entry.at,
        messageId: turn.messageId ?? entry.messageId,
        text: [turn.text, entry.text].filter(Boolean).join('\n\n'),
        streaming: entry.streaming,
      }

    case 'artefact-marker':
      return {
        ...turn,
        at: turn.at ?? entry.at,
        messageId: turn.messageId ?? entry.messageId,
        artefacts: [...turn.artefacts, entry],
      }

    // Nothing to show — the interrupt renders the question itself — but it marks where the agent
    // spoke, so the turn it ended can be rated.
    case 'interrupt':
      return { ...turn, at: turn.at ?? entry.at, messageId: turn.messageId ?? entry.messageId }

    case 'notice':
      return { ...turn, notices: [...turn.notices, entry] }

    case 'answer':
      return { ...turn, answers: [...turn.answers, entry] }
  }
}

/** What a retry asks again: the last thing the maker wrote or answered, or a plain go-on when the
 * transcript holds neither. */
export function retryPrompt(transcript: readonly HarnessTranscriptEntry[]): string {
  const said = transcript.findLast((entry) => entry.type === 'user' || entry.type === 'answer')

  // A message that was only its files has no words to ask again.
  return (said && 'text' in said && said.text) || 'Continue.'
}
